/**
 * 消費一覧・消費詳細の表示の言葉と値の形（プロダクト仕様書「表示の制御」）。
 * クレジットは小数2桁、S3容量は小数3桁、割合と構成比は小数1桁で四捨五入し、クレジット・円・時点値は3桁ごとにカンマで区切る。
 * 値がないときは「-」。
 */

import type { CategoryColumns } from '@/lib/api/credit-consumption';
import type { Failure } from '@/lib/api/credit-contracts';
import { formatDecimal, groupDigits } from '@/lib/credit-consumption/decimal';

/** 消費項目（列挙型定義書の「日本語」） */
export const itemLabels: Record<string, string> = {
  StoreSku: '店舗×SKU',
  S3Capacity: 'S3容量',
  UserCount: 'ユーザー数',
  DataConnect: 'データコネクト',
  StoreAgent: 'ストアエージェント',
  Athena: 'Athena',
  Bedrock: 'Bedrock',
  Fargate: 'Fargate',
  Glue: 'Glue',
  SageMaker: 'SageMaker',
};

/** 表示区分（列挙型定義書の「日本語」） */
export const categoryLabels: Record<string, string> = {
  DataVolume: 'データ量',
  UserCount: 'ユーザー数',
  DataConnect: 'データコネクト',
  StoreAgent: 'ストアエージェント',
  Compute: '計算量',
  FkToken: 'FKトークン',
};

export const NONE = '-';

export const credit = (v: string | null | undefined) => (v ? formatDecimal(v, 2) : NONE);
export const percent = (v: string | null | undefined) => (v ? formatDecimal(v, 1) : NONE);
export const terabytes = (v: string | null | undefined) => (v ? formatDecimal(v, 3) : NONE);
/** 円・時点値（整数） */
export const integer = (v: string | null | undefined) => (v ? groupDigits(v) : NONE);

const reflectedFormat = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** 反映時点（日本時間の `YYYY/MM/DD HH:mm`）。なければ「-」 */
export function reflectedText(iso: string | null): string {
  if (!iso) return NONE;
  const p = Object.fromEntries(
    reflectedFormat.formatToParts(new Date(iso)).map((x) => [x.type, x.value]),
  );
  return `${p.year}/${p.month}/${p.day} ${p.hour}:${p.minute}`;
}

/** 消費の内訳の1列：消費項目か、表示区分の合計 */
export interface BreakdownColumn {
  key: string;
  /** 上の段の見出し（表示区分の名前）。見出しが1段のときは null */
  group: string | null;
  label: string;
  value: (items: Record<string, string>, totals: Record<string, string>) => string | null;
}

/**
 * 表示区分ごとにまとめた消費の内訳の列。表示区分と消費項目の名前が同じときは見出しを1段にし、
 * 消費項目が2つ以上属する表示区分には、その最後に合計の列を置く
 */
export function breakdownColumns(categories: CategoryColumns[]): BreakdownColumn[] {
  return categories.flatMap((c) => {
    const groupLabel = categoryLabels[c.category] ?? c.category;
    const single = c.items.length === 1 && (itemLabels[c.items[0]] ?? c.items[0]) === groupLabel;
    const items: BreakdownColumn[] = c.items.map((item) => ({
      key: `item:${item}`,
      group: single ? null : groupLabel,
      label: itemLabels[item] ?? item,
      value: (items) => items[item] ?? null,
    }));
    if (c.items.length >= 2) {
      items.push({
        key: `category:${c.category}`,
        group: groupLabel,
        label: '合計',
        value: (_, totals) => totals[c.category] ?? null,
      });
    }
    return items;
  });
}

/** 上の段の見出しを、続く列の数とともに並べる（1段の見出しは null） */
export function headerGroups(columns: BreakdownColumn[]): { group: string | null; span: number }[] {
  const out: { group: string | null; span: number }[] = [];
  for (const c of columns) {
    const last = out[out.length - 1];
    if (last && c.group !== null && last.group === c.group) last.span += 1;
    else out.push({ group: c.group, span: 1 });
  }
  return out;
}

/** 取得に失敗したときの文言 */
export function fetchFailureMessage(f: Failure): string {
  const reason = f.message.replace(/。$/, '');
  switch (f.code) {
    case 'CREDIT_DB_UNAVAILABLE':
      return 'credit DBに接続できませんでした。少し待ってから、もう一度取得してください。';
    case 'COMMON_DB_UNAVAILABLE':
      return 'CommonDBから取得できませんでした。少し待ってから、もう一度取得してください。';
    case 'COMPANY_NOT_FOUND':
      return 'この企業はCommonDBにありません。';
    case 'NETWORK':
      return `サーバーに接続できませんでした（${reason}）。`;
    default:
      return `取得できませんでした（${reason}）。`;
  }
}
